import React, { useState, useEffect, useCallback } from 'react';
import { Volume2, VolumeX, Square, Loader2 } from 'lucide-react';
import { Button } from './Button';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';
import { Slider } from './Slider';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from './Select';
import {
  getVoiceForPersona,
  cleanTextForTTS,
  INWORLD_VOICES,
  InworldVoiceId,
} from '../helpers/useInworldTTS';
import { KITTEN_VOICES } from '../helpers/useKittenTTS';
import { useTTS } from '../helpers/useTTS';
import styles from './AiVoiceControls.module.css';

interface AiVoiceControlsProps {
  className?: string;
  onMuteChange?: (muted: boolean) => void;
}

// Global mute state (persisted in localStorage)
const MUTE_KEY = 'ai-voice-muted';
const VOLUME_KEY = 'ai-voice-volume';
const VOICE_KEY = 'ai-voice-selected';

export const AiVoiceControls: React.FC<AiVoiceControlsProps> = ({ className, onMuteChange }) => {
  const { isPlaying, isSpeaking, stop, error } = useTTS();

  const [isMuted, setIsMuted] = useState(() => {
    if (typeof window === 'undefined') return true;
    const saved = localStorage.getItem(MUTE_KEY);
    return saved === null ? true : saved === 'true'; // Default muted
  });

  const [volume, setVolume] = useState(() => {
    if (typeof window === 'undefined') return 0.8;
    const saved = localStorage.getItem(VOLUME_KEY);
    return saved ? parseFloat(saved) : 0.8;
  });

  const [selectedVoice, setSelectedVoice] = useState(() => {
    if (typeof window === 'undefined') return 'auto';
    return localStorage.getItem(VOICE_KEY) || 'auto';
  });

  // Persist mute state
  useEffect(() => {
    localStorage.setItem(MUTE_KEY, String(isMuted));
    onMuteChange?.(isMuted);
  }, [isMuted, onMuteChange]);

  // Persist volume
  useEffect(() => {
    localStorage.setItem(VOLUME_KEY, String(volume));
  }, [volume]);

  // Persist voice selection
  useEffect(() => {
    localStorage.setItem(VOICE_KEY, selectedVoice);
  }, [selectedVoice]);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
    if (!isMuted) {
      // Muting - stop any current playback
      stop();
    }
  }, [isMuted, stop]);

  return (
    <div className={`${styles.container} ${className || ''}`}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={toggleMute}
            className={`${styles.muteButton} ${isMuted ? styles.muted : ''}`}
          >
            {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{isMuted ? 'Unmute AI voices' : 'Mute AI voices'}</TooltipContent>
      </Tooltip>

      {isPlaying && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon-sm" onClick={stop} className={styles.stopButton}>
              <Square size={14} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Stop speaking</TooltipContent>
        </Tooltip>
      )}

      {isSpeaking && !isPlaying && <Loader2 size={14} className={styles.loadingIcon} />}
    </div>
  );
};

// Hook to check if voice is muted
export function useAiVoiceMuted(): boolean {
  const [isMuted, setIsMuted] = useState(() => {
    if (typeof window === 'undefined') return true;
    const saved = localStorage.getItem(MUTE_KEY);
    return saved === null ? true : saved === 'true'; // Default muted
  });

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === MUTE_KEY) {
        setIsMuted(e.newValue === 'true');
      }
    };

    // Also listen for custom events from same window
    const handleMuteChange = () => {
      setIsMuted(localStorage.getItem(MUTE_KEY) === 'true');
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('ai-voice-mute-changed', handleMuteChange);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('ai-voice-mute-changed', handleMuteChange);
    };
  }, []);

  return isMuted;
}

// Hook to get the user-selected voice preference
export function useSelectedVoice(): string {
  const [voice, setVoice] = useState(() => {
    if (typeof window === 'undefined') return 'auto';
    return localStorage.getItem(VOICE_KEY) || 'auto';
  });

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === VOICE_KEY) {
        setVoice(e.newValue || 'auto');
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  return voice;
}

// Component to speak a message from an AI participant
interface AiParticipantSpeakerProps {
  text: string;
  personaId: string | number;
  personaName?: string;
  autoSpeak?: boolean;
  onSpeakStart?: () => void;
  onSpeakEnd?: () => void;
}

export const AiParticipantSpeaker: React.FC<AiParticipantSpeakerProps> = ({
  text,
  personaId,
  personaName,
  autoSpeak = false,
  onSpeakStart,
  onSpeakEnd,
}) => {
  const { speak, stop, isPlaying, isSpeaking } = useTTS();
  const isMuted = useAiVoiceMuted();
  const hasSpokenRef = React.useRef(false);

  // Auto-speak when message appears (if enabled and not muted)
  useEffect(() => {
    if (autoSpeak && !isMuted && !hasSpokenRef.current && text) {
      hasSpokenRef.current = true;
      const cleanedText = cleanTextForTTS(text);
      if (cleanedText.length > 10) {
        const voiceId = getVoiceForPersona(personaId, personaName);
        onSpeakStart?.();
        speak(cleanedText, { voiceId }).finally(() => {
          onSpeakEnd?.();
        });
      }
    }
  }, [autoSpeak, isMuted, text, personaId, personaName, speak, onSpeakStart, onSpeakEnd]);

  // Stop on unmount
  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  const handleSpeak = useCallback(() => {
    if (isMuted) return;

    if (isPlaying) {
      stop();
      return;
    }

    const cleanedText = cleanTextForTTS(text);
    if (cleanedText.length > 0) {
      const voiceId = getVoiceForPersona(personaId, personaName);
      onSpeakStart?.();
      speak(cleanedText, { voiceId }).finally(() => {
        onSpeakEnd?.();
      });
    }
  }, [isMuted, isPlaying, text, personaId, personaName, speak, stop, onSpeakStart, onSpeakEnd]);

  if (isMuted) return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={handleSpeak}
          className={styles.speakButton}
          disabled={isSpeaking && !isPlaying}
        >
          {isSpeaking && !isPlaying ? (
            <Loader2 size={12} className={styles.loadingIcon} />
          ) : isPlaying ? (
            <Square size={12} />
          ) : (
            <Volume2 size={12} />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{isPlaying ? 'Stop' : `Listen to ${personaName || 'AI'}`}</TooltipContent>
    </Tooltip>
  );
};
